import type { FC } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/helpers/utils'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'
import { SubmissionFormFooter } from './submission-form-footer'

interface SubmissionFooterButtonProps {
	label: string
	disabled?: boolean
	isLoading?: boolean
}

type SubmissionFooterLinkProps = SubmissionFooterButtonProps & {
	to: string
	form?: never
}

type SubmissionFooterFormProps = SubmissionFooterButtonProps & {
	form: string
	to?: never
}

type SubmissionFooterProps =
	| SubmissionFooterLinkProps
	| SubmissionFooterFormProps

export const SubmissionFooter: FC<SubmissionFooterProps> = ({
	label,
	disabled,
	to,
	form,
	isLoading
}) => {
	const { t } = useTranslation()
	const baseClassName = 'w-full m:w-max'
	const backToOverviewPath =
		routes.LUXE_SUBMISSIONS_MM_SUBMISSIONS.handle.fullPath

	const backButton = (
		<>
			<ButtonLinkLegacy
				className={cn(baseClassName, 'm:hidden')}
				size='lg'
				to={backToOverviewPath}
				variant='outline'
			>
				{t('luxe_submissions:back_to_overview')}
			</ButtonLinkLegacy>
			<ButtonLinkLegacy
				className={cn(baseClassName, 'max-m:hidden')}
				size='sm'
				to={backToOverviewPath}
				variant='outline'
			>
				{t('luxe_submissions:back_to_overview')}
			</ButtonLinkLegacy>
		</>
	)

	if (to) {
		return (
			<SubmissionFormFooter className='flex m:flex-row flex-col items-center m:justify-between gap-3'>
				{backButton}
				<ButtonLinkLegacy
					className={cn(baseClassName, 'm:hidden')}
					disabled={disabled}
					size='lg'
					to={to}
					variant='primary'
				>
					{label}
				</ButtonLinkLegacy>
				<ButtonLinkLegacy
					className={cn(baseClassName, 'max-m:hidden')}
					disabled={disabled}
					size='md'
					to={to}
					variant='primary'
				>
					{label}
				</ButtonLinkLegacy>
			</SubmissionFormFooter>
		)
	}

	return (
		<SubmissionFormFooter className='flex m:flex-row flex-col items-center m:justify-between gap-3'>
			{backButton}
			<ButtonLegacy
				className={cn(baseClassName, 'm:hidden')}
				disabled={disabled}
				form={form}
				size='lg'
				type='submit'
				variant='primary'
			>
				{label}
				{isLoading && <Spinner />}
			</ButtonLegacy>
			<ButtonLegacy
				className={cn(baseClassName, 'max-m:hidden')}
				disabled={disabled}
				form={form}
				size='md'
				type='submit'
				variant='primary'
			>
				{label}
				{isLoading && <Spinner />}
			</ButtonLegacy>
		</SubmissionFormFooter>
	)
}
